/**
 * Simple country detection utility for market-specific Shopify redirects
 * Provides privacy-friendly, offline country detection using timezone
 */

/**
 * Mapping of IANA timezone identifiers to ISO country codes
 * Covers major timezones for supported markets: US, GB, AE, SG, AU, and EU countries
 */
const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  // United States
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
  'America/Anchorage': 'US',
  'America/Honolulu': 'US',
  'America/Detroit': 'US',
  'America/Indiana/Indianapolis': 'US',
  'America/Kentucky/Louisville': 'US',
  'America/Kentucky/Monticello': 'US',
  'America/North_Dakota/Beulah': 'US',
  'America/North_Dakota/Center': 'US',
  'America/North_Dakota/New_Salem': 'US',
  'US/Eastern': 'US',
  'US/Central': 'US',
  'US/Mountain': 'US',
  'US/Pacific': 'US',
  'US/Alaska': 'US',
  'US/Hawaii': 'US',

  // Australia
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU',
  'Australia/Adelaide': 'AU',
  'Australia/Darwin': 'AU',
  'Australia/Hobart': 'AU',
  'Australia/Canberra': 'AU',
  'Australia/Lord_Howe': 'AU',

  // United Kingdom
  'Europe/London': 'GB',
  GMT: 'GB',
  'Europe/Belfast': 'GB',
  'Europe/Edinburgh': 'GB',
  'Europe/Cardiff': 'GB',

  // United Arab Emirates
  'Asia/Dubai': 'AE',
  'Asia/Muscat': 'AE',

  // Singapore
  'Asia/Singapore': 'SG',

  // European Union Countries
  'Europe/Vienna': 'AT',
  'Europe/Brussels': 'BE',
  'Europe/Sofia': 'BG',
  'Europe/Zurich': 'CH',
  'Europe/Prague': 'CZ',
  'Europe/Copenhagen': 'DK',
  'Europe/Tallinn': 'EE',
  'Europe/Helsinki': 'FI',
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Munich': 'DE',
  'Europe/Athens': 'GR',
  'Europe/Zagreb': 'HR',
  'Europe/Budapest': 'HU',
  'Atlantic/Reykjavik': 'IS',
  'Europe/Dublin': 'IE',
  'Europe/Rome': 'IT',
  'Europe/Vatican': 'IT',
  'Europe/Riga': 'LV',
  'Europe/Vilnius': 'LT',
  'Europe/Amsterdam': 'NL',
  'Europe/Oslo': 'NO',
  'Europe/Warsaw': 'PL',
  'Europe/Lisbon': 'PT',
  'Atlantic/Madeira': 'PT',
  'Atlantic/Azores': 'PT',
  'Europe/Bucharest': 'RO',
  'Europe/Bratislava': 'SK',
  'Europe/Ljubljana': 'SI',
  'Europe/Madrid': 'ES',
  'Atlantic/Canary': 'ES',
  'Africa/Ceuta': 'ES',
  'Europe/Stockholm': 'SE',

  // Common timezone aliases
  CET: 'DE',
  EET: 'GR',
  WET: 'PT',
} as const;

/**
 * Simple country detection function that tries timezone detection first,
 * then falls back to Australia (AU) as the default
 *
 * @returns ISO country code (2-letter), guaranteed to return a value
 *
 * @example
 * ```typescript
 * const country = detectUserCountry();
 * // Returns 'US' for America/New_York, 'AU' for unknown timezones
 * ```
 */
export function detectUserCountry(): string {
  try {
    // Get the user's timezone using the Intl API
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (timezone) {
      // Look up the country code for this timezone
      const countryCode = TIMEZONE_TO_COUNTRY[timezone];

      if (countryCode) {
        console.info(`Country detection: ${timezone} -> ${countryCode}`);
        return countryCode;
      } else {
        console.warn(
          `Country detection: Unknown timezone '${timezone}', defaulting to AU`,
        );
      }
    } else {
      console.warn(
        'Country detection: No timezone available, defaulting to AU',
      );
    }
  } catch (error) {
    console.warn('Country detection failed:', error, 'defaulting to AU');
  }

  // Default to Australia
  return 'AU';
}
